import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
    bulkSavePublicHolidays,
    confirmHolidayYear,
    deletePublicHoliday,
    fetchHolidayYears,
    fetchPublicHolidays,
    savePublicHoliday,
    unconfirmHolidayYear,
} from "../../../api/requests/holiday-requests";

/**
 * Resmî tatil takviminin yıl bazında yönetimi.
 *
 * <p>Her işaretleme anında sunucuya yazılır; "kaydet" düğmesi yoktur. Takvim
 * yılda birkaç kez ve az sayıda günle düzenlendiği için toplu kaydetme, yarım
 * kalmış bir düzenlemenin sessizce kaybolması riskini getirirdi.</p>
 */
export const usePublicHolidays = (initialYear) => {
    const [year, setYear] = useState(initialYear || new Date().getFullYear());
    const [holidays, setHolidays] = useState([]);
    const [years, setYears] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const loadHolidays = useCallback(async (targetYear) => {
        setLoading(true);
        try {
            const json = await fetchPublicHolidays(targetYear);
            if (json?.statusCode === 200) {
                setHolidays(json.data || []);
            } else {
                toast.error(json?.message || "Tatil takvimi yüklenemedi");
            }
        } catch (error) {
            toast.error("Tatil takvimi yüklenemedi");
        } finally {
            setLoading(false);
        }
    }, []);

    const loadYears = useCallback(async () => {
        try {
            const json = await fetchHolidayYears();
            if (json?.statusCode === 200) setYears(json.data || []);
        } catch (error) {
            // Yıl durumu ikincil bilgidir; takvim yine de kullanılabilir.
            console.error("Yıl durumu alınamadı", error);
        }
    }, []);

    useEffect(() => {
        loadHolidays(year);
    }, [year, loadHolidays]);

    useEffect(() => {
        loadYears();
    }, [loadYears]);

    /** Tarih (YYYY-MM-DD) → tatil kaydı. Takvim hücreleri bununla boyanır. */
    const holidayByDate = useMemo(() => {
        const map = {};
        holidays.forEach((h) => {
            if (h.holidayDate) map[h.holidayDate] = h;
        });
        return map;
    }, [holidays]);

    const yearStatus = useMemo(
        () => years.find((y) => y.year === year) || null,
        [years, year]
    );

    const addHoliday = async (date, description) => {
        setSaving(true);
        try {
            const json = await savePublicHoliday({ holidayDate: date, description });
            if (json?.statusCode === 200) {
                toast.success("Tatil günü eklendi");
                await loadHolidays(year);
                await loadYears();
                return true;
            }
            toast.error(json?.message || "Tatil günü eklenemedi");
            return false;
        } catch (error) {
            toast.error("Tatil günü eklenemedi");
            return false;
        } finally {
            setSaving(false);
        }
    };

    /**
     * Bir aralığın tamamını tek işlemde yazar (bayram blokları için).
     *
     * <p>Toplu uç yıl bazlı çalıştığı ve listeyi bütün olarak doğruladığı için
     * kayıtlar yıla göre gruplanıp gönderilir; aralık yıl sınırını aşarsa iki
     * istek olur. Her istek kendi içinde ya tamamen yazılır ya hiç yazılmaz.</p>
     *
     * @param {Array<{holidayDate:string, description:string}>} items
     */
    const addHolidayRange = async (items) => {
        if (!items.length) return false;

        const byYear = items.reduce((acc, item) => {
            const y = Number(item.holidayDate.slice(0, 4));
            (acc[y] = acc[y] || []).push(item);
            return acc;
        }, {});

        setSaving(true);
        try {
            for (const [y, holidays] of Object.entries(byYear)) {
                const json = await bulkSavePublicHolidays({
                    year: Number(y),
                    holidays,
                    replaceYear: false,
                    confirmYear: false,
                });
                if (json?.statusCode !== 200) {
                    toast.error(json?.message || "Tatil aralığı kaydedilemedi");
                    await loadHolidays(year);
                    return false;
                }
            }
            toast.success(`${items.length} gün tatil olarak işaretlendi`);
            await loadHolidays(year);
            await loadYears();
            return true;
        } catch (error) {
            toast.error("Tatil aralığı kaydedilemedi");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const removeHoliday = async (date) => {
        setSaving(true);
        try {
            const json = await deletePublicHoliday(date);
            if (json?.statusCode === 200) {
                toast.success("Tatil günü kaldırıldı");
                await loadHolidays(year);
                await loadYears();
                return true;
            }
            toast.error(json?.message || "Tatil günü kaldırılamadı");
            return false;
        } catch (error) {
            toast.error("Tatil günü kaldırılamadı");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const toggleConfirmation = async () => {
        setSaving(true);
        try {
            const json = yearStatus?.confirmed
                ? await unconfirmHolidayYear(year)
                : await confirmHolidayYear(year);
            if (json?.statusCode === 200) {
                toast.success(json.message);
                await loadYears();
                return true;
            }
            toast.error(json?.message || "İşlem tamamlanamadı");
            return false;
        } catch (error) {
            toast.error("İşlem tamamlanamadı");
            return false;
        } finally {
            setSaving(false);
        }
    };

    return {
        year,
        setYear,
        holidays,
        holidayByDate,
        yearStatus,
        years,
        loading,
        saving,
        addHoliday,
        addHolidayRange,
        removeHoliday,
        toggleConfirmation,
    };
};
